import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase/supabaseClient';
import { useAuthModal } from '../Components/Auth/AuthModalManager';
import Loader from '../Components/ReusableComponents/Loader'
import Header from '../Components/ReusableComponents/Header'
import '../Styles/subscriptions.scss';

function Subscriptions() {
  const { user, hasActiveLicense, hadActiveLicense } = useAuth();
  const { openSubscribe, openLicenseUpgrade, openLogin } = useAuthModal();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [license, setLicense] = useState(null);
  const [figmaAccessKey, setFigmaAccessKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        setLoading(true);
        await Promise.all([
          fetchUserSubscription(),
          fetchUserLicense(),
          fetchFigmaAccessKey() 
        ]);
        setLoading(false);
      };

      fetchData();
    }
  }, [user]);

  const fetchUserSubscription = async () => {
    try {
      // setLoading(true);

      // Get subscription from Supabase using email matching
      const { data: subscriptionData, error: supabaseError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('customer_email', user.email)
        .in('status', ['active', 'on_trial', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(); //  this avoids 406 + PGRST11

      if (supabaseError && supabaseError.code !== 'PGRST116') {
        throw supabaseError;
      }

      setSubscription(subscriptionData);

    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription data');
    } 
    // finally {
    //   setLoading(false);
    // }
  };

  const fetchUserLicense = async () => {
    try {
      const { data: licenseData, error: licenseError } = await supabase
        .from('user_licenses')
        .select('*')
        .eq('user_id', user.id)
        .gt('expires_at', new Date().toISOString())
        .order('expires_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (licenseError && licenseError.code !== 'PGRST116') {
        throw licenseError;
      }

      setLicense(licenseData);

    } catch (err) {
      console.error('Error fetching license:', err);
    }
  };

  const fetchFigmaAccessKey = async () => {
    try {
      const { data: accessData, error: accessError } = await supabase
        .from('figma_access')
        .select('figma_access_key, access_type, expires_at, is_active')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (accessError && accessError.code !== 'PGRST116') {
        throw accessError;
      }

      // Check if 24h access hasn't expired
      if (accessData) {
        if (accessData.expires_at) {
          const now = new Date();
          const expiry = new Date(accessData.expires_at);
          if (now > expiry) {
            setFigmaAccessKey(null);
            return;
          }
        }
        setFigmaAccessKey(accessData.figma_access_key);
      }

    } catch (err) {
      console.error('Error fetching Figma access key:', err);
    }
  };

  const handleCopyKey = async () => {
    if (figmaAccessKey) {
      try {
        await navigator.clipboard.writeText(figmaAccessKey);
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const difference = expiry - now;

    if (difference <= 0) return 'Expired';

    const hours = Math.floor(difference / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m remaining`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'cancelled':
        return 'status-cancelled';
      case 'expired':
        return 'status-expired';
      case 'past_due':
        return 'status-past-due';
      default:
        return 'status-default';
    }
  };

  const handleManageSubscription = () => {
    // Direct users to LemonSqueezy customer portal
    // They can search by email to find their subscription
    const customerPortalUrl = `https://app.lemonsqueezy.com/my-orders`;
    window.open(customerPortalUrl, '_blank');
  };

  // const handleSubscribeNow = () => {
  //   setShowPopup(true);
  // };
  const handleSubscribeNow = () => {
    // Use the appropriate modal based on license status
    if (hadActiveLicense) {
      openLicenseUpgrade();
    } else {
      openSubscribe();
    }
  };

  const refreshPage = () => {
    // Redirect to your subscription page
    window.location.href = '/subscriptions';
  };

  // if logged out
  if (!user) {
    return (
      <div className="subscriptions">
        <div className="content content--no-subscription">
          <Header
            titleNeutral="Subscriptions"
            text="Please sign in to view your subscriptions."
          />
          <button onClick={openLogin} className="subscribe-btn pri-btn">
            Log in/Sign up
          </button>
        </div>
      </div>
    );
  }



  if (loading) {
    return (
      <div className="subscriptions">
        <div className="content content--loading">
          <Header
            titleNeutral="Subscriptions"
          />
          <Loader />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscriptions">
        <div className="content content--error">
          <Header
            titleNeutral="Subscriptions"
            text={error}
          />
          <button onClick={refreshPage} className="retry-btn pri-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // if logged in & no subscription and no 24h license
  if (!loading && !subscription && !license) {
    return (
      <div className="subscriptions">
        <div className="content content--no-subscription">
          <Header
            titleNeutral="Subscriptions"
            text="You don't have an active subscription yet. Please subscribe below to unlock all premium features."
          />
          <button onClick={handleSubscribeNow} className="subscribe-btn pri-btn">
            Subscribe
          </button>
        </div>

      </div>
    );
  }

  // if logged in and 24 hours access
  if (!subscription && license) {
    return (
      <div className='subscriptions'>
        <Header
          titleNeutral="24-Hour Access"
          text="You have temporary access to all premium features."
        />

        <div className="subscriptions-overview license-card">
          <div className="card-header">
            <h3>24-Hour Access</h3>
            <span className="status-badge status-active">
              Active
            </span>
          </div>

          <div className="card-content">
            <div className="time-remaining-highlight">
              <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#847F78">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>

              <span className="time-remaining">{getTimeRemaining(license.expires_at)}</span>
            </div>

            <div className="plan-started">
              <span className="label">Access Started:</span>
              <span className="value">{formatDateTime(license.created_at)}</span>
            </div>

            <div className="plan-expires">
              <span className="label">Access Expires:</span>
              <span className="value">{formatDateTime(license.expires_at)}</span>
            </div>

            <div className="plan-type">
              <span className="label">Type:</span>
              <span className="value">24-Hour Access</span>
            </div>

            <div className="figma-key">
              <span className="label">Figma Access Key:</span>
              <div className="key-display">
                <span className="value">{figmaAccessKey}</span>
                <button onClick={handleCopyKey} className="copy-key-btn">
                  {keyCopied ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <svg width={18} height={18} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="card-footer">
            <p className="upgrade-message">
              Enjoying the premium features?<br /> Get unlimited access and subscribe.
            </p>
          </div>

          <button
            onClick={handleSubscribeNow}
            className="manage-subscription-btn pri-btn"
          >
            Upgrade to Subscription
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='subscriptions'>

      <Header
        titleNeutral="Subscriptions"
        text="Please see below for an overview of your subscription details."
      />



      <div className="subscriptions-overview">
        <div className="card-header">
          <h3>Subscription</h3>
          <span className={`status-badge ${getStatusColor(subscription.status)}`}>
            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
          </span>
        </div>

        <div className="card-content">
          <div className="plan-started">
            <span className="label">Subscription started:</span>
            <span className="value">{formatDate(subscription.created_at)}</span>
          </div>

          <div className="plan-email">
            <span className="label">Email:</span>
            <span className="value">{subscription.customer_email}</span>
          </div>

          <div className="plan-id">
            <span className="label">Subscription ID:</span>
            <span className="value">{subscription.lemon_squeezy_id}</span>
          </div>

          <div className="figma-key">
            <span className="label">Figma Access Key:</span>
            <div className="key-display">
              <span className="value">{figmaAccessKey}</span>
              <button onClick={handleCopyKey} className="copy-key-btn">
                {keyCopied ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  </>
                ) : (
                  <>
                    <svg width={18} height={18} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={handleManageSubscription}
          className="manage-subscription-btn pri-btn"
        >
          Manage or cancel your subscription
        </button>
      </div>

      <div className="subscription-notes">
        <p><strong>Note:</strong> All subscription management is handled securely through LemonSqueezy. You'll be redirected to their customer portal where you can:</p>
        <ul>
          <li>Update your payment methods</li>
          <li>View detailed billing history</li>
          <li>Download invoices</li>
          <li>Cancel your subscription</li>
          <li>Update billing address</li>
        </ul>
      </div>
    </div>

  );
}

export default Subscriptions;